import { useFlag } from '@calendly/feature-check-react'
import { I18n } from '@calendly/i18n'
import { BodyText, TitleText } from '@calendly/ui/components/typography'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'

import { useNotetakerSettingsContext } from 'features/meeting_intelligence/settings/notetaker_settings_provider'

import { IntegrationsBody } from './integrations_body'
import { RecordingDefaultsBody } from './recording_defaults_body'
import { RecordingRulesSwitch } from './recording_rules_switch'
import {
  SectionContentWrapper,
  SectionTitleWrapper,
  SectionWrapper,
  TitleWrapper,
  SectionRightContentRow,
  ConnectedWrapper,
  MobileSectionTitleWrapper,
  MobileTitleWrapper,
  MobileSectionWrapper,
} from './styles'

// Calendar type display name mapping
// To add a new calendar type, simply add an entry here
const CALENDAR_TYPE_DISPLAY_NAMES = {
  outlook: 'Outlook Calendar',
  google: 'Google Calendar',
  // Add more calendar types here as needed
  // Example: icloud: 'iCloud Calendar',
  //          exchange: 'Exchange Calendar',
}

const getCalendarTypeDisplayName = (kind) => {
  if (!kind) return 'UNKNOWN CALENDAR TYPE'
  return CALENDAR_TYPE_DISPLAY_NAMES[kind.toLowerCase()] || 'UNKNOWN CALENDAR TYPE'
}

const getDictionary = (kind) => {
  const calendarType = getCalendarTypeDisplayName(kind)
  return {
    calendlyMeetings: {
      title: I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.calendly_meetings.title'),
      description: I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.calendly_meetings.description')
    },
    connectedNonCalendlyMeetings: {
      title: I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.connected_meetings.title'),
      description: I18n.t(
        'publisher.personal_settings.meeting_recaps.recording_rules.connected_meetings.description',
        { calendar_type: calendarType }
      )
    },
    externalInviteeMeetings: {
      title: I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.external_meetings.title'),
      description: I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.external_meetings.description')
    },
    autoShareRecapEmails: {
      title: I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.auto_share_recap_emails.title'),
      description: I18n.t(
        'publisher.personal_settings.meeting_recaps.recording_rules.auto_share_recap_emails.description'
      )
    },
    integrations: {
      title: I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.integrations.title'),
      description: I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.integrations.description')
    }
  }
}

// Main settings component which contains all settings sections.
// autoShareRecapEmails is sourced from NotetakerSettingsProvider (useNotetakerSettingsContext).
export const NotetakerSettings = ({ kind } = {}) => {
  const isDesktop = useDeviceType('isDesktopUp')
  const automatedRecapSharingEnabled = useFlag('automated_recap_sharing')
  const integrationsCardEnabled = useFlag('integrations_card')
  const { autoShareRecapEmails, setAutoShareRecapEmails } = useNotetakerSettingsContext()
  const dictionary = getDictionary(kind)

  return isDesktop ? (
    <SectionContentWrapper data-testid="recording-rules-desktop">
      <SectionWrapper>
        <SectionTitleWrapper>
          <TitleText size="small">
            {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults_section_title')}
          </TitleText>
        </SectionTitleWrapper>
        <RecordingDefaultsBody />
      </SectionWrapper>
      {automatedRecapSharingEnabled && (
        <SectionWrapper>
          <SectionTitleWrapper>
            <TitleText size="small">
              {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recap_sharing_section_title')}
            </TitleText>
          </SectionTitleWrapper>
          <SectionRightContentRow>
            <TitleWrapper>
              <BodyText size="medium" weight="bold">
                {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.auto_share_recap_emails.title')}
              </BodyText>
              <BodyText size="medium" color="muted">
                {I18n.t(
                  'publisher.personal_settings.meeting_recaps.recording_rules.auto_share_recap_emails.description'
                )}
              </BodyText>
            </TitleWrapper>
            <RecordingRulesSwitch
              value={autoShareRecapEmails}
              onChange={setAutoShareRecapEmails}
            />
          </SectionRightContentRow>
        </SectionWrapper>
      )}
      {integrationsCardEnabled && (
        <SectionWrapper>
          <SectionTitleWrapper>
            <TitleText size="small">{dictionary.integrations.title}</TitleText>
            <BodyText color="muted" size="medium">
              {dictionary.integrations.description}
            </BodyText>
          </SectionTitleWrapper>
          <ConnectedWrapper>
            <IntegrationsBody />
          </ConnectedWrapper>
        </SectionWrapper>
      )}
    </SectionContentWrapper>
  ) : (
    <SectionContentWrapper data-testid="recording-rules-mobile">
      <MobileSectionWrapper>
        <MobileSectionTitleWrapper>
          <TitleText size="small">
            {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recording_defaults_section_title')}
          </TitleText>
        </MobileSectionTitleWrapper>
        <RecordingDefaultsBody />
      </MobileSectionWrapper>
      {automatedRecapSharingEnabled && (
        <MobileSectionWrapper>
          <MobileSectionTitleWrapper>
            <TitleText size="small">
              {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.recap_sharing_section_title')}
            </TitleText>
          </MobileSectionTitleWrapper>
          <MobileTitleWrapper>
            <TitleWrapper>
              <BodyText size="medium" weight="bold">{dictionary.autoShareRecapEmails.title}</BodyText>
              <BodyText size="medium" color="muted">{dictionary.autoShareRecapEmails.description}</BodyText>
            </TitleWrapper>
            <RecordingRulesSwitch
              value={autoShareRecapEmails}
              onChange={setAutoShareRecapEmails}
            />
          </MobileTitleWrapper>
        </MobileSectionWrapper>
      )}
      {integrationsCardEnabled && (
        <MobileSectionWrapper>
          <MobileSectionTitleWrapper>
            <TitleText size="small">
              {dictionary.integrations.title}
            </TitleText>
            <BodyText size="medium" color="muted">{dictionary.integrations.description}</BodyText>
          </MobileSectionTitleWrapper>
          <IntegrationsBody />
        </MobileSectionWrapper>
      )}
    </SectionContentWrapper>
  )
}
